// This module is isomorphic (used by server and client). Do not mark as server-only.

import { isRecord, readNumber, readString } from '@sweetistics/contracts/utils/type-guards';

export interface SerializedError {
  cause?: SerializedError;
  code?: string | number;
  context?: Record<string, unknown>;
  message: string;
  name: string;
  stack?: string;
  statusCode?: number;
  timestamp?: string;
}

/**
 * Enhanced error serialization with metadata and cause chain traversal
 */
export function serializeError(error: unknown, context?: Record<string, unknown>): SerializedError {
  const timestamp = new Date().toISOString();

  if (error instanceof Error) {
    const serialized: SerializedError = {
      name: error.name,
      message: error.message,
      timestamp,
    };
    assignOptionalField(serialized, 'stack', cleanStackTrace(error.stack));
    assignOptionalField(serialized, 'context', context);
    const codeCandidate = readCode(readExtraField(error, 'code'));
    if (codeCandidate !== undefined) {
      serialized.code = codeCandidate;
    }
    const statusCodeCandidate =
      readNumber(readExtraField(error, 'statusCode')) ?? readNumber(readExtraField(error, 'status'));
    if (statusCodeCandidate !== undefined) {
      serialized.statusCode = statusCodeCandidate;
    }
    const causeValue = readExtraField(error, 'cause');
    if (causeValue !== undefined && causeValue !== null) {
      serialized.cause = serializeError(causeValue);
    }

    return serialized;
  }

  // Handle non-Error objects
  if (isRecord(error)) {
    type LooseRecord = Record<string, unknown> & {
      message?: unknown;
      stack?: unknown;
      code?: unknown;
      statusCode?: unknown;
      status?: unknown;
      name?: unknown;
    };
    const looseRecord = error as LooseRecord;
    const message = readString(looseRecord.message) ?? JSON.stringify(error);
    const stack = cleanStackTrace(readString(looseRecord.stack));
    const code = readCode(looseRecord.code);
    const statusCode = readNumber(looseRecord.statusCode) ?? readNumber(looseRecord.status);
    const serialized: SerializedError = {
      name: readString(looseRecord.name) ?? 'UnknownError',
      message,
      timestamp,
    };
    assignOptionalField(serialized, 'stack', stack);
    assignOptionalField(serialized, 'context', context);
    if (code !== undefined) {
      serialized.code = code;
    }
    if (typeof statusCode === 'number') {
      serialized.statusCode = statusCode;
    }
    return serialized;
  }

  // Handle primitive values
  const fallback: SerializedError = {
    name: 'UnknownError',
    message: String(error),
    timestamp,
  };
  if (context) {
    fallback.context = context;
  }
  return fallback;
}

/**
 * Clean stack traces by removing Next.js internal frames and limiting length
 */
export function cleanStackTrace(stack?: string): string | undefined {
  if (!stack || stack.length === 0) {
    return;
  }

  const lines = stack.split('\n');
  const cleaned: string[] = [];

  const dropPatterns = [
    '/.next/server/',
    '/node_modules/.pnpm/next@',
    '/node_modules/next/dist/',
    'app-route-turbo-experimental.runtime',
    'base-server.js',
    'router-server.js',
    'start-server.js',
    'next-dev-server.js',
    'trace/trace.js',
    'node:internal',
  ];

  for (const line of lines) {
    // Always keep the first line (error name/message)
    if (cleaned.length === 0) {
      cleaned.push(line);
      continue;
    }

    const trimmed = line.trim();

    // Keep non-stack-trace lines
    if (!trimmed.startsWith('at ')) {
      cleaned.push(line);
      continue;
    }

    // Drop noisy internal frames
    if (dropPatterns.some((pattern) => line.includes(pattern))) {
      continue;
    }

    cleaned.push(line);
  }

  // Limit to reasonable number of frames
  const MAX_FRAMES = 15;
  return cleaned.slice(0, MAX_FRAMES).join('\n');
}

/**
 * Specialized serializer for HTTP errors
 */
export function serializeHttpError(
  error: unknown,
  context?: {
    method?: string;
    url?: string;
    headers?: Record<string, string>;
    statusCode?: number;
  }
): SerializedError {
  const serialized = serializeError(error, context);

  // Enhance with HTTP context
  const statusCode = context?.statusCode;
  if (typeof statusCode === 'number') {
    serialized.statusCode = statusCode;
  }

  return serialized;
}

/**
 * Specialized serializer for database errors
 */
export function serializeDatabaseError(
  error: unknown,
  context?: {
    query?: string;
    params?: unknown[];
    table?: string;
    operation?: string;
  }
): SerializedError {
  const serialized = serializeError(error, context);

  // Don't include sensitive query params in logs
  const serializedContext = serialized.context;
  if (serializedContext !== undefined && serializedContext !== null && isRecord(serializedContext)) {
    type SerializedContextRecord = Record<string, unknown> & { params?: unknown };
    const contextRecord = serializedContext as SerializedContextRecord;
    const rawParameters = contextRecord.params;
    let parametersCount = 0;
    if (Array.isArray(rawParameters)) {
      parametersCount = rawParameters.length;
    } else if (rawParameters !== null && rawParameters !== undefined) {
      parametersCount = 1;
    }
    const { params: _omittedParameters, ...rest } = serializedContext;
    serialized.context = { ...rest, paramsCount: parametersCount };
  }

  return serialized;
}

const assignOptionalField = <T extends object, K extends keyof T>(
  target: T,
  key: K,
  value: T[K] | undefined | null
): void => {
  if (value !== undefined && value !== null) {
    target[key] = value;
  }
};

const readCode = (value: unknown): string | number | undefined =>
  typeof value === 'string' || typeof value === 'number' ? value : undefined;

const readExtraField = (error: Error, key: PropertyKey): unknown => {
  const propertyKey: PropertyKey = key;
  if (!Object.hasOwn(error, propertyKey)) {
    return;
  }
  return Object.getOwnPropertyDescriptor(error, propertyKey)?.value;
};
